import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
];

interface LanguageSwitcherProps {
  userId?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ userId }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Load language preference from user profile on mount
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (userId) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('language_preference')
            .eq('id', userId)
            .single();

          if (!error && data?.language_preference) {
            i18n.changeLanguage(data.language_preference);
          }
        } catch (error) {
          console.error('Error loading user language preference:', error);
        }
      }
    };

    loadUserLanguage();
  }, [userId, i18n]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = async (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);

    // Save to user profile if logged in
    if (userId) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ language_preference: langCode })
          .eq('id', userId);

        if (error) {
          console.error('Error saving language preference:', error);
        }
      } catch (error) {
        console.error('Error updating language preference:', error);
      }
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium
          bg-white/10 dark:bg-white/5 hover:bg-white/20 dark:hover:bg-white/10
          text-luna-text-light dark:text-luna-text-dark
          border border-white/20 dark:border-white/10"
        aria-label="Change language"
        type="button"
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="hidden sm:inline">{currentLanguage.name}</span>
        <span className="material-symbols-outlined text-sm">
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-luna-primary rounded-lg shadow-lg 
          border border-luna-text-light/10 dark:border-luna-text-dark/20 z-50 overflow-hidden">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors
                ${i18n.language === lang.code
                  ? 'bg-luna-secondary/10 text-luna-secondary font-semibold'
                  : 'text-luna-text-light dark:text-luna-text-dark hover:bg-luna-text-light/5 dark:hover:bg-luna-text-dark/5'
                }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span>{lang.name}</span>
              {i18n.language === lang.code && (
                <span className="material-symbols-outlined text-sm ml-auto">check</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;

